/*
  Dwell-triggered autoplay for the sports page's product carousels.

  One engine for every rail on the page: the sports brand bands, the
  automatic sale row and the trending rail all mark their scroll track
  `data-epg-rail` and their section `data-epg-rail-scope`, and get the same
  behaviour, so the five cannot drift.

  The rule the owner asked for is dwell, not entry: a rail starts moving
  only once the reader has actually had it in front of them. So an
  IntersectionObserver arms a one-second timer when enough of the rail is on
  screen, and the rail only starts advancing if it is still on screen when
  that timer runs out. Scrolling straight past a band never starts it.

  It gives way three ways, and they are not the same thing:

  - **Paused** while the pointer or the focus is inside one of the rail's
    own hold regions -- its track and its controls, each marked
    `data-epg-rail-hold` -- and again while the rail is off screen or the
    tab is hidden. A background tab is not served frames, so an interval
    left ticking there queues a run of scrolls that all land at once on
    return. The hold is deliberately *not* the whole section: a brand
    band's section is a full-bleed hero over its rail, so it fills the
    window, and holding on that stopped all three bands dead.
  - **Retired** for the rest of the page load the moment the reader takes
    the rail over -- an arrow, a swipe, a sideways wheel, an arrow key.
    Wrestling a rail back off somebody six seconds after they moved it is
    the behaviour carousels are disliked for, and retiring instead means the
    arrows are a genuine stop, which is most of what WCAG 2.2.2 is asking
    for. There is no labelled pause control; see CLAUDE.md.
  - **Off entirely** under `prefers-reduced-motion: reduce`, which is
    re-checked live rather than only at load, so turning the setting on
    stops every rail on the page without a reload.

  A *vertical* wheel over a rail is the reader scrolling the page, not
  driving the carousel, and is deliberately ignored -- treating it as
  control killed a rail the reader had never touched.
*/
(function () {
  'use strict';

  /* Every block that owns a rail asks for this file, so a sports page with
     three brand bands, the sale row and the trending rail carries five
     copies of the tag. The browser fetches it once but runs it five times,
     so the engine claims the page and the later runs fall straight out. */
  if (window.__epgRailAuto) return;
  window.__epgRailAuto = true;

  var SELECTOR = '[data-epg-rail]';
  var DWELL = 1000; /* ms on screen before the first advance */
  var INTERVAL = 3500; /* ms between advances */

  var rails = [];

  /* No block emits `data-epg-rail-dwell` -- the second is the owner's spec
     and is not a per-block decision. It is read anyway so the dwell can be
     varied from the markup without editing this file, which is how the
     behaviour is driven under test. */
  function num(el, attr, fallback) {
    var raw = parseInt(el.getAttribute(attr), 10);
    return isNaN(raw) || raw < 0 ? fallback : raw;
  }

  var motionQuery =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  function reduced() {
    return !!(motionQuery && motionQuery.matches);
  }

  function Rail(track) {
    this.track = track;
    this.scope = track.closest('[data-epg-rail-scope]') || track.parentElement || track;
    this.dwell = num(track, 'data-epg-rail-dwell', DWELL);
    this.interval = num(track, 'data-epg-rail-interval', INTERVAL);
    this.dwellTimer = null;
    this.tickTimer = null;
    this.visible = false;
    this.hover = false; /* pointer inside one of the hold regions */
    this.focused = false; /* focus inside one of them */
    this.retired = false; /* the reader has driven it; it does not come back */
    this.paused = false; /* the reader pressed pause; only pressing play undoes it */
    this.toggle = this.scope.querySelector('[data-epg-rail-toggle]');
    this.bind();
  }

  /* One card plus the gap, measured rather than assumed: every rail on the
     page sizes its cards in vw, and the tablet and mobile breakpoints show
     a different number of them. */
  Rail.prototype.step = function () {
    var card = this.track.firstElementChild;
    if (!card) return this.track.clientWidth * 0.8;
    var gap = parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  };

  Rail.prototype.advance = function () {
    var track = this.track;
    var max = track.scrollWidth - track.clientWidth;
    if (max < 2) return;
    if (track.scrollLeft >= max - 2) {
      track.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      track.scrollBy({ left: this.step(), behavior: 'smooth' });
    }
  };

  Rail.prototype.runnable = function () {
    return (
      this.visible &&
      !this.hover &&
      !this.focused &&
      !this.retired &&
      !this.paused &&
      !reduced() &&
      document.visibilityState === 'visible'
    );
  };

  /* WCAG 2.2.2 wants a control, not a convention: a reader has to be able to
     stop this and have it stay stopped, and holding on hover is a mouse's
     privilege while retiring on a swipe is something you discover by
     accident. So every autoplaying rail carries a pause control beside its
     arrows.

     It ships hidden and is revealed here, which is what keeps it honest: it
     appears only where there is something to pause -- a rail that overflows,
     an engine that loaded, and motion the reader has not already asked us to
     stop. Reduced motion never binds an interval, so the control never
     appears there rather than offering to pause what was never moving. */
  Rail.prototype.reflect = function () {
    var toggle = this.toggle;
    if (!toggle) return;
    var offerable = !reduced() && this.track.scrollWidth > this.track.clientWidth + 2;
    toggle.hidden = !offerable;
    if (!offerable) return;
    var playing = !this.paused && !this.retired;
    toggle.setAttribute('data-state', playing ? 'playing' : 'paused');
    var label = toggle.getAttribute(playing ? 'data-label-pause' : 'data-label-play');
    if (label) toggle.setAttribute('aria-label', label);
  };

  Rail.prototype.start = function () {
    if (this.tickTimer) return;
    var self = this;
    this.tickTimer = window.setInterval(function () {
      /* Re-checked every tick, so a reduced-motion setting turned on
         mid-session stops the rail at the next beat even if nothing else
         on the page moves. */
      if (!self.runnable()) return self.stop();
      self.advance();
    }, this.interval);
  };

  Rail.prototype.stop = function () {
    if (this.tickTimer) {
      window.clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
    if (this.dwellTimer) {
      window.clearTimeout(this.dwellTimer);
      this.dwellTimer = null;
    }
  };

  /* Every path in and out of motion goes through here, so "on screen",
     "not held", "not retired", "motion allowed" and "waited out the dwell"
     can only ever be satisfied together. */
  Rail.prototype.sync = function () {
    if (!this.runnable()) return this.stop();
    if (this.tickTimer || this.dwellTimer) return;
    var self = this;
    this.dwellTimer = window.setTimeout(function () {
      self.dwellTimer = null;
      /* Re-check: the reader may have scrolled on, or taken hold of the
         rail, during the second we were waiting. */
      if (self.runnable()) self.start();
    }, this.dwell);
  };

  Rail.prototype.retire = function () {
    this.retired = true;
    this.stop();
    this.reflect();
  };

  Rail.prototype.bind = function () {
    var self = this;
    var scope = this.scope;

    /* The hold is the track and the rail's own controls -- NOT the whole
       section. The track alone is too small: the arrows sit outside the
       scroller, and a rail that kept advancing while the reader reached for
       "next" would move the card out from under the click. But the section
       is far too big. A brand band's section is a full-bleed hero standing
       over its rail, so it fills the window at any scroll position where the
       rail is on screen -- a cursor resting anywhere on the page was inside
       it. `pointerenter` fired, `pointerleave` never did, and all three
       bands sat still for the whole visit while the sale row, whose section
       is only a head and a track, ran perfectly. That is the bug this
       narrowing fixes; don't widen it back to the scope.

       Blocks mark the regions that should hold with `data-epg-rail-hold`.
       A rail that marks none falls back to its scope, which is what every
       rail did before. */
    var marked = scope.querySelectorAll('[data-epg-rail-hold]');
    var holds;
    if (marked.length) {
      /* The track is always part of the hold whether or not it is marked --
         it is the rail. Blocks only have to name the extra regions, which is
         one fewer attribute to forget. Nesting is safe: `pointerenter` and
         `pointerleave` fire on every element entered and left, so a marked
         wrapper and the track inside it both count in and both count out. */
      holds = Array.prototype.slice.call(marked);
      if (holds.indexOf(this.track) === -1) holds.push(this.track);
    } else {
      holds = [scope];
    }
    this.holds = holds;

    /* Counted rather than set and cleared, because the regions are siblings:
       crossing from the control row into the carousel fires a leave and an
       enter, and a bare boolean would flicker to false in between. */
    var inside = 0;

    /* Mouse and pen only. A touch fires `pointerenter` too, and if its
       matching `pointerleave` is ever missed the rail would be held for
       good; on touch the retire below is what gives way instead. */
    var pointerly = function (e) {
      return !e.pointerType || e.pointerType === 'mouse' || e.pointerType === 'pen';
    };

    var focusHeld = function () {
      var active = document.activeElement;
      if (!active) return false;
      for (var i = 0; i < holds.length; i++) {
        if (holds[i].contains(active)) return true;
      }
      return false;
    };

    for (var h = 0; h < holds.length; h++) {
      holds[h].addEventListener('pointerenter', function (e) {
        if (!pointerly(e)) return;
        inside++;
        self.hover = true;
        self.sync();
      });
      holds[h].addEventListener('pointerleave', function (e) {
        if (!pointerly(e)) return;
        inside = Math.max(0, inside - 1);
        self.hover = inside > 0;
        self.sync();
      });
    }

    /* Focus is watched on the scope so a tab that lands outside every hold
       region is still seen, and then measured against the hold regions --
       otherwise focus on the hero's call to action would freeze the rail
       exactly the way the pointer used to. */
    scope.addEventListener('focusin', function () {
      self.focused = focusHeld();
      self.sync();
    });
    scope.addEventListener('focusout', function () {
      /* The new focus target is not active yet when this fires. */
      window.setTimeout(function () {
        self.focused = focusHeld();
        self.sync();
      }, 0);
    });

    /* Taking the rail over retires it. A press anywhere in the section is
       deliberate -- an arrow, a card, the view-all link -- and so is a key
       press or a sideways wheel.

       The pause control is the exception, and it has to be: the press that
       works it lands inside the scope, so retiring on it would fire before
       the click, and the button would then find the rail already stopped and
       read the press as "play". Pressing pause would have started the rail.
       The control governs itself; everything else in the section retires. */
    var fromToggle = function (e) {
      return !!(self.toggle && e.target && self.toggle.contains(e.target));
    };
    scope.addEventListener(
      'pointerdown',
      function (e) {
        if (fromToggle(e)) return;
        self.retire();
      },
      { passive: true }
    );
    scope.addEventListener(
      'keydown',
      function (e) {
        if (fromToggle(e)) return;
        self.retire();
      },
      { passive: true }
    );
    this.track.addEventListener(
      'wheel',
      function (e) {
        /* Sideways only. A vertical wheel over the rail is the reader
           scrolling the page past it, and retiring on that killed rails
           nobody had touched. */
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) self.retire();
      },
      { passive: true }
    );

    document.addEventListener('visibilitychange', function () {
      self.sync();
    });

    /* The control is inside the scope, so the `pointerdown` above retires the
       rail before this runs. Pressing play therefore has to lift both the
       pause and that retirement, or the button would do nothing on the press
       that was meant to start it again. */
    if (this.toggle) {
      this.toggle.addEventListener('click', function () {
        if (!self.paused && !self.retired) {
          self.paused = true;
          self.stop();
        } else {
          self.paused = false;
          self.retired = false;
          self.sync();
        }
        self.reflect();
      });
    }

    window.addEventListener('resize', function () {
      self.reflect();
    });
    this.reflect();

    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          /* Half the rail, or half the viewport's worth of it: a rail
             taller than the window can never reach a 0.5 ratio, and on a
             phone the card row is the tallest thing on the page. */
          self.visible =
            entry.isIntersecting &&
            (entry.intersectionRatio >= 0.5 ||
              entry.intersectionRect.height >= window.innerHeight * 0.5);
          self.sync();
        });
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    io.observe(this.track);
  };

  function init() {
    var tracks = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < tracks.length; i++) {
      if (tracks[i].hasAttribute('data-epg-rail-bound')) continue;
      tracks[i].setAttribute('data-epg-rail-bound', '');
      rails.push(new Rail(tracks[i]));
    }
  }

  /* Live, not only at load: a reader who turns reduced motion on partway
     down the page should not have to reload to stop the rails. The rails
     are still bound either way, so turning it back off starts them again
     rather than needing the engine re-run. */
  if (motionQuery) {
    var onMotionChange = function () {
      for (var i = 0; i < rails.length; i++) {
        rails[i].sync();
        rails[i].reflect();
      }
    };
    if (motionQuery.addEventListener) motionQuery.addEventListener('change', onMotionChange);
    else if (motionQuery.addListener) motionQuery.addListener(onMotionChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The theme editor tears a section down and rebuilds it on every setting
     change, so a rail added after load still has to be picked up. */
  document.addEventListener('shopify:section:load', init);
})();
